import type { CeoInflowsAnalyticsSummary } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import {
  formatMillions,
  formatMoney,
  INFLOW_STATUS_LABELS,
} from '../ceoFormat';
import {
  CustomerNameText,
  DocNumberText,
  FunnelCard,
  FunnelCardLabel,
  FunnelCardSubtext,
  FunnelCardValue,
  FunnelGrid,
  FunnelSuccessValue,
  FunnelWarningValue,
  OverdueHighlight,
  ProjectNameText,
  ReceivableBalanceCell,
  ReceivedCell,
  Section,
  SectionHeader,
  SectionLink,
  SectionSubtext,
  SectionTitle,
  Table,
  TableWrap,
} from './styles';

interface InflowsFunnelSectionProps {
  infSum: CeoInflowsAnalyticsSummary;
}

export function InflowsFunnelSection({ infSum }: InflowsFunnelSectionProps) {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>
            Поступления месяца: План БДДС vs Подписанные КС-3 vs Фактически поступили
          </SectionTitle>
          <SectionSubtext>
            Сквозной контроль конверсии выполнения СМР в живые деньги на расчётных счетах с учётом
            зачёта авансов и гарантийных удержаний
          </SectionSubtext>
        </div>
        <SectionLink to="/agents/ceo/inflows">
          Детальная сверка БДДС и КС-3 →
        </SectionLink>
      </SectionHeader>

      <FunnelGrid>
        <FunnelCard>
          <FunnelCardLabel>
            1. ПЛАНИРУЕМЫЕ ПОСТУПЛЕНИЯ ПО БДДС
          </FunnelCardLabel>
          <FunnelCardValue>
            {formatMillions(infSum.total_bdds_planned)}
          </FunnelCardValue>
          <FunnelCardSubtext>
            Предъявлено КС-2 Заказчикам: {formatMillions(infSum.total_ks2_submitted)}
          </FunnelCardSubtext>
        </FunnelCard>

        <FunnelCard>
          <FunnelCardLabel>
            2. ОЖИДАЕМЫЕ ПО ПОДПИСАННЫМ КС-3 (НЕТТО)
          </FunnelCardLabel>
          <FunnelCardValue>
            {formatMillions(infSum.total_expected_net_ks3)}
          </FunnelCardValue>
          <FunnelCardSubtext>
            КС-3 брутто: {formatMillions(infSum.total_ks3_signed_gross)} (минус аванс{' '}
            {formatMillions(infSum.total_advance_deduction)} и ГУ{' '}
            {formatMillions(infSum.total_retention_deduction)})
          </FunnelCardSubtext>
        </FunnelCard>

        <FunnelCard $highlight>
          <FunnelCardLabel>
            3. ФАКТИЧЕСКИ ПОСТУПИЛИ НА СЧЕТА
          </FunnelCardLabel>
          <FunnelSuccessValue>
            {formatMillions(infSum.total_actual_received)}
          </FunnelSuccessValue>
          <FunnelCardSubtext>
            Исполнение БДДС: <strong>{infSum.bdds_fulfillment_pct.toFixed(1)}%</strong> · Оплата
            подписанных КС-3: <strong>{infSum.ks3_collection_pct.toFixed(1)}%</strong>
          </FunnelCardSubtext>
        </FunnelCard>

        <FunnelCard>
          <FunnelCardLabel>
            4. ОЖИДАЕТ ЗАЧИСЛЕНИЯ ПО ПОДПИСАННЫМ КС-3
          </FunnelCardLabel>
          <FunnelWarningValue>
            {formatMillions(infSum.ks3_awaiting_cash)}
          </FunnelWarningValue>
          <FunnelCardSubtext>
            В т.ч. просроченная дебиторка по КС-3:{' '}
            <OverdueHighlight $hasOverdue={infSum.overdue_ks3_receivables > 0}>
              {formatMillions(infSum.overdue_ks3_receivables)}
            </OverdueHighlight>
          </FunnelCardSubtext>
        </FunnelCard>
      </FunnelGrid>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Строительный объект / Заказчик</th>
              <th>Договор / Документ КС-3</th>
              <th>План БДДС</th>
              <th>Подписано КС-3 (брутто)</th>
              <th>Ожидается по КС-3 (нетто)</th>
              <th>Фактически поступило</th>
              <th>Остаток к поступлению</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {infSum.rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <ProjectNameText>{r.project_name}</ProjectNameText>
                  <CustomerNameText>{r.customer_name}</CustomerNameText>
                </td>
                <td>
                  <div>{r.contract_number}</div>
                  <DocNumberText>
                    {r.ks3_doc_number ?? 'Ожидается выпуск КС-3'}
                  </DocNumberText>
                </td>
                <td>{formatMoney(r.bdds_planned_amount)}</td>
                <td>
                  {r.ks3_signed_amount > 0 ? formatMoney(r.ks3_signed_amount) : '—'}
                </td>
                <td>
                  <strong>{formatMoney(r.expected_net_inflow)}</strong>
                </td>
                <ReceivedCell $hasReceived={r.actual_received_amount > 0}>
                  <strong>{formatMoney(r.actual_received_amount)}</strong>
                </ReceivedCell>
                <ReceivableBalanceCell>
                  {r.ks3_receivable_balance > 0 ? formatMoney(r.ks3_receivable_balance) : '0 ₽'}
                </ReceivableBalanceCell>
                <td>
                  <Badge
                    $tone={
                      r.status === 'paid_in_full'
                        ? 'success'
                        : r.status === 'overdue_receivable'
                          ? 'danger'
                          : r.status === 'partially_paid'
                            ? 'info'
                            : 'warning'
                    }
                  >
                    {INFLOW_STATUS_LABELS[r.status] ?? r.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
